import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { API_ENDPOINTS } from '../config/api';
import { useAuth } from '../hooks/useAuth';

interface XeroContextType {
  isAuthenticated: boolean;
  accessToken: string | null;
  authenticate: () => void;
  handleCallback: (code: string) => Promise<void>;
  checkAuthentication: () => Promise<boolean>;
}

const XeroContext = createContext<XeroContextType | undefined>(undefined);

export function XeroProvider({ children }: { children: React.ReactNode }) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const { user } = useAuth();

  const CLIENT_ID = '27F0806EF1BE45EC863107A990B4CFD6';
  const REDIRECT_URI = 'https://overwatch.nyedis.com/xero-callback';
  const SCOPES = 'projects openid profile email offline_access';

  useEffect(() => {
    console.log('👤 Current user state:', { 
      isUserDefined: !!user,
      username: user?.username
    });
  }, [user]);

  useEffect(() => {
    console.log('👂 Setting up Xero message listener');
    
    const handleMessage = (event: MessageEvent) => {
      console.log('📨 Received message:', event.data);
      
      if (event.data.type === 'XERO_AUTH_SUCCESS') {
        console.log('🎉 Xero authentication success message received');
        console.log('⏰ Message timestamp:', event.data.timestamp);
        window.location.reload();
      } else if (event.data.type === 'XERO_AUTH_ERROR') {
        console.error('❌ Xero authentication error message received:', {
          error: event.data.error,
          timestamp: event.data.timestamp,
          details: event.data.details
        });
      }
    };

    window.addEventListener('message', handleMessage);
    console.log('✅ Message listener attached');

    return () => {
      console.log('🧹 Cleaning up Xero message listener');
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const authenticate = useCallback(() => {
    console.log('🔑 Starting Xero authentication');
    console.log('👤 User state when starting auth:', {
      isUserDefined: !!user,
      username: user?.username
    });

    const authUrl = `https://login.xero.com/identity/connect/authorize?response_type=code&client_id=${CLIENT_ID}&redirect_uri=${encodeURIComponent(REDIRECT_URI)}&scope=${encodeURIComponent(SCOPES)}`;
    const width = 600;
    const height = 600;
    const left = window.innerWidth / 2 - width / 2;
    const top = window.innerHeight / 2 - height / 2;

    console.log('🔗 Opening auth window with URL:', authUrl);
    window.open(
      authUrl,
      'xero-auth',
      `width=${width},height=${height},left=${left},top=${top}`
    );
  }, [user]);

  const handleCallback = useCallback(async (code: string) => {
    console.log('🔐 Starting Xero callback handling');
    console.log('👤 User state in callback:', {
      isUserDefined: !!user,
      username: user?.username
    });
    console.log('📦 Received code length:', code.length);
    console.log('🔍 Code value:', code);
    
    try {
      // Wait for user to be defined if it isn't already
      if (!user?.username) {
        console.log('⏳ User not found, waiting briefly for auth to initialize...');
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        if (!user?.username) {
          console.error('❌ User still not authenticated after waiting');
          throw new Error('User not authenticated');
        }
      }

      console.log('✅ User authenticated:', user.username);
      const endpoint = API_ENDPOINTS.XERO.TOKEN_EXCHANGE;
      const requestBody = { code, userId: user.username };
      
      console.log('🚀 Making token exchange request:', {
        url: endpoint,
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        payload: requestBody
      });
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody),
      }).catch(error => {
        console.error('🔥 Network error during fetch:', error);
        throw error;
      });

      console.log('📡 Token exchange response received');
      console.log('📊 Response status:', response.status);
      console.log('📋 Response headers:', Object.fromEntries(response.headers.entries()));
      
      if (!response.ok) {
        const errorText = await response.text().catch(e => 'Could not read error response');
        console.error('❌ Token exchange failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText,
          headers: Object.fromEntries(response.headers.entries())
        });
        throw new Error(`Token exchange failed: ${errorText}`);
      }

      const data = await response.json().catch(error => {
        console.error('🔥 Error parsing JSON response:', error);
        throw error;
      });
      
      console.log('✅ Token exchange successful');
      console.log('📋 Token response structure:', {
        keys: Object.keys(data),
        tokenLength: data.access_token?.length
      });
      
      setAccessToken(data.access_token);
      setIsAuthenticated(true);
      
      if (window.opener) {
        console.log('📤 Sending success message to opener');
        window.opener.postMessage({
          type: 'XERO_AUTH_SUCCESS',
          timestamp: new Date().toISOString()
        }, '*');
      }
      
      window.close();
    } catch (error) {
      console.error('❌ Error in handleCallback:', error);
      console.error('📋 Error details:', {
        name: error instanceof Error ? error.name : 'Unknown',
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined
      });
      
      if (window.opener) {
        console.log('📤 Sending error message to opener');
        window.opener.postMessage({
          type: 'XERO_AUTH_ERROR',
          error: error instanceof Error ? error.message : String(error),
          timestamp: new Date().toISOString(),
          details: {
            errorType: error instanceof Error ? error.name : 'Unknown',
            errorStack: error instanceof Error ? error.stack : undefined
          }
        }, '*');
      }
      
      window.close();
      throw error;
    }
  }, [user]);

  const checkAuthentication = useCallback(async () => {
    console.log('🔍 Checking Xero authentication');
    console.log('👤 User state during check:', {
      isUserDefined: !!user,
      username: user?.username
    });

    if (isAuthenticated && accessToken) {
      console.log('✅ Already authenticated with access token');
      return true;
    }

    try {
      if (!user?.username) {
        console.log('❌ No user found during auth check');
        return false;
      }

      const response = await fetch(`${API_ENDPOINTS.XERO.PROJECTS}?userId=${user.username}`);
      const isAuth = response.ok;
      console.log(`${isAuth ? '✅' : '❌'} Auth check result:`, isAuth);
      setIsAuthenticated(isAuth);
      return isAuth;
    } catch (error) {
      console.error('❌ Auth check error:', error);
      setIsAuthenticated(false);
      return false;
    }
  }, [isAuthenticated, accessToken, user]);

  return (
    <XeroContext.Provider
      value={{
        isAuthenticated,
        accessToken,
        authenticate,
        handleCallback,
        checkAuthentication,
      }}
    >
      {children}
    </XeroContext.Provider>
  );
}

export function useXero() {
  const context = useContext(XeroContext);
  if (context === undefined) {
    throw new Error('useXero must be used within a XeroProvider');
  }
  return context;
}
